import {
  type CrossClientQueryAtomicClause,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
  type CrossClientQueryExecution,
  type CrossClientQueryResolverStage,
  CrossClientQueryResolverStages,
  type CrossClientQueryResult,
  type CrossClientQueryResultAnswer,
  type CrossClientQueryResultBucketCount,
  CrossClientQueryResultEvaluationTypes,
  type CrossClientQueryResultMatch,
  CrossClientQueryResultMatches,
  type CrossClientQueryResultSource,
  type CrossClientQueryResultStatus,
  CrossClientQueryResultStatuses,
  CROSS_CLIENT_MATCH_NONE,
} from '../../../../models/CrossClient'
import { type User } from '../../../../models/User'
import { awaitsResolvers, owesLocalStage } from '../../../../store/crossClient/utils'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'

export const CcqCards = {
  MATCHED: 'matched',
  CLOSER: 'closer',
  NOT_IN_RECORDS: 'notInRecords',
  NOT_MATCHED: 'notMatched',
  MISSING_DATA: 'missingData',
} as const

export type CcqCard = (typeof CcqCards)[keyof typeof CcqCards]

export const ccqCriterionTitle = (clause: CrossClientQueryAtomicClause): string => clause.questionForm || clause.description

export const ccqCriteria = (clauses: CrossClientQueryAtomicClause[]): string[] => clauses.map(ccqCriterionTitle)

export interface CcqCriterionClause extends CrossClientQueryAtomicClause {
  // `NOT x` in the formula: shown with the NNF's wording, but its answers are still about `x`.
  isNegated: boolean
}

export const ccqCriterionClauses = ({
  decomposition,
  nnfDecomposition,
}: {
  decomposition?: Pick<CrossClientQueryLogicalQuestionDecomposition, 'atomicClauses'> | null
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition | null
}): CcqCriterionClause[] =>
  (decomposition?.atomicClauses ?? []).map((clause) => {
    const nnfClauses = nnfDecomposition?.atomicClauses ?? []
    const negatedVariable = nnfDecomposition?.negatedClauses?.[clause.variable]
    const negated = nnfClauses.find((each) => each.variable === negatedVariable)
    // Also asked plainly elsewhere in the formula: a row reads one way, so it keeps the plain one.
    const isAlsoPlain = nnfClauses.some((each) => each.variable === clause.variable)
    if (negated === undefined || isAlsoPlain) {
      return { ...clause, isNegated: false }
    }
    return { ...clause, description: negated.description, questionForm: negated.questionForm, isNegated: true }
  })

export const CCQ_CARD_ORDER: CcqCard[] = [CcqCards.MATCHED, CcqCards.CLOSER, CcqCards.NOT_IN_RECORDS, CcqCards.NOT_MATCHED, CcqCards.MISSING_DATA]

export const sumCounts = (counts: CcqCounts): number => CCQ_CARD_ORDER.reduce((total, card) => total + counts[card], 0)

export type CcqCounts = Record<CcqCard, number>

export const EMPTY_COUNTS: CcqCounts = { matched: 0, closer: 0, notInRecords: 0, notMatched: 0, missingData: 0 }

const CCQ_DEEP_ANALYSIS_CARDS: CcqCard[] = [CcqCards.CLOSER, CcqCards.NOT_IN_RECORDS]

export const ccqOffersDeepAnalysis = (card: CcqCard): boolean => CCQ_DEEP_ANALYSIS_CARDS.includes(card)

interface CardRow {
  status: CrossClientQueryResultStatus
  match?: CrossClientQueryResultMatch | null
}

// TODO(ccq): Potential and Remaining stand in for "Needs a closer look" and "Not in records" until the backend can tell "no value found" apart.
export const cardForResult = ({ status, match }: CardRow): CcqCard | null => {
  if (status === CrossClientQueryResultStatuses.NO_DATA) {
    return CcqCards.MISSING_DATA
  }
  if (status === CrossClientQueryResultStatuses.SUCCEEDED) {
    return match === CrossClientQueryResultMatches.MATCH ? CcqCards.MATCHED : match === CrossClientQueryResultMatches.NO_MATCH ? CcqCards.NOT_MATCHED : null
  }
  if (status === CrossClientQueryResultStatuses.INITIALIZED) {
    return match === CrossClientQueryResultMatches.POTENTIAL ? CcqCards.CLOSER : match === null || match === undefined ? CcqCards.NOT_IN_RECORDS : null
  }
  return null
}

export interface CcqCardFilter {
  status: CrossClientQueryResultStatus
  match?: (CrossClientQueryResultMatch | typeof CROSS_CLIENT_MATCH_NONE)[]
  lastResolverStage?: CrossClientQueryResolverStage[]
}

const CHECKED_STAGES: CrossClientQueryResolverStage[] = [CrossClientQueryResolverStages.BULK, CrossClientQueryResolverStages.LOCAL]
const LOCAL_STAGE: CrossClientQueryResolverStage[] = [CrossClientQueryResolverStages.LOCAL]

// Mirrors awaitsResolvers: while anything is in flight, a result counts as checked only once it has the stage it still owes.
const stageWhileInFlight = (card: CcqCard, execution: CrossClientQueryExecution): CrossClientQueryResolverStage[] => {
  const match = card === CcqCards.CLOSER ? CrossClientQueryResultMatches.POTENTIAL : null
  return owesLocalStage(execution, match) ? LOCAL_STAGE : CHECKED_STAGES
}

export const MATCHED_CARD_FILTER: CcqCardFilter = { status: CrossClientQueryResultStatuses.SUCCEEDED, match: [CrossClientQueryResultMatches.MATCH] }

export const cardFilter = (card: CcqCard, execution: CrossClientQueryExecution, inFlight: boolean): CcqCardFilter => {
  if (card === CcqCards.MATCHED) {
    return MATCHED_CARD_FILTER
  }
  if (card === CcqCards.NOT_MATCHED) {
    return { status: CrossClientQueryResultStatuses.SUCCEEDED, match: [CrossClientQueryResultMatches.NO_MATCH] }
  }
  if (card === CcqCards.MISSING_DATA) {
    return { status: CrossClientQueryResultStatuses.NO_DATA }
  }
  const match = card === CcqCards.CLOSER ? CrossClientQueryResultMatches.POTENTIAL : CROSS_CLIENT_MATCH_NONE
  const base: CcqCardFilter = { status: CrossClientQueryResultStatuses.INITIALIZED, match: [match] }
  return inFlight ? { ...base, lastResolverStage: stageWhileInFlight(card, execution) } : base
}

// Unlike the card counts: once resolvers stop, the clients they never reached still land in Not in records.
export const countReachedByResolvers = (buckets: CrossClientQueryResultBucketCount[]): number =>
  buckets.reduce((sum, bucket) => (bucket.status === CrossClientQueryResultStatuses.INITIALIZED && !bucket.lastResolverStage ? sum : sum + bucket.count), 0)

export const cardCounts = (buckets: CrossClientQueryResultBucketCount[], execution: CrossClientQueryExecution | null, inFlight: boolean): CcqCounts =>
  buckets.reduce<CcqCounts>((counts, bucket) => {
    const card = cardForResult(bucket)
    if (card === null || awaitsResolvers(execution, bucket, inFlight)) {
      return counts
    }
    return { ...counts, [card]: counts[card] + bucket.count }
  }, EMPTY_COUNTS)

export const CcqCriterionStates = { MET: 'met', NOT_MET: 'notMet', NOT_FOUND: 'notFound', NOT_CHECKED: 'notChecked' } as const

export type CcqCriterionState = (typeof CcqCriterionStates)[keyof typeof CcqCriterionStates]

export interface CcqCriterionView {
  clause: CcqCriterionClause
  state: CcqCriterionState
  explanation: string
  // A decided criterion's explanation, or AI Deep Analysis's own words when it couldn't decide.
  showsExplanation: boolean
  source: CrossClientQueryResultSource | null
}

const hasDefinitiveAnswer = (answers: CrossClientQueryResultAnswer[] | null | undefined): boolean => (answers ?? []).some((answer) => answer.decision !== null)

const pickAnswers = ({ answers, bulkResolverAnswers, localResolverAnswers, match }: CrossClientQueryResult): CrossClientQueryResultAnswer[] => {
  const localOrBulk =
    match === CrossClientQueryResultMatches.POTENTIAL && localResolverAnswers != null && !hasDefinitiveAnswer(localResolverAnswers)
      ? bulkResolverAnswers
      : localResolverAnswers
  return answers ?? localOrBulk ?? bulkResolverAnswers ?? []
}

// TODO(ccq): replace the explanation match with the backend's skipped flag once it records which criteria the check short-circuited.
const SKIPPED_EXPLANATION = /based on other criteria/i

const isDecided = (match: CrossClientQueryResultMatch | null | undefined): boolean =>
  match === CrossClientQueryResultMatches.MATCH || match === CrossClientQueryResultMatches.NO_MATCH

const stateFor = (answer: CrossClientQueryResultAnswer, match: CrossClientQueryResultMatch | null | undefined, isNegated: boolean): CcqCriterionState => {
  if (answer.decision !== null) {
    const isMet = isNegated ? !answer.decision : answer.decision
    return isMet ? CcqCriterionStates.MET : CcqCriterionStates.NOT_MET
  }
  return SKIPPED_EXPLANATION.test(answer.explanation) || (!answer.source && isDecided(match)) ? CcqCriterionStates.NOT_CHECKED : CcqCriterionStates.NOT_FOUND
}

const NO_ANSWER: CrossClientQueryResultAnswer = { decision: null, explanation: '' }

export const criterionStates = (result: CrossClientQueryResult, clauses: CcqCriterionClause[]): CcqCriterionView[] => {
  const answers = pickAnswers(result)
  return clauses.map((clause, index) => {
    const answer = answers[index] ?? NO_ANSWER
    const hasDecision = answer.decision !== null
    const isAiExplained = result.evaluationType === CrossClientQueryResultEvaluationTypes.AI && answer.explanation.trim() !== ''
    return {
      clause,
      state: stateFor(answer, result.match, clause.isNegated),
      explanation: answer.explanation,
      showsExplanation: hasDecision || isAiExplained,
      source: answer.source ?? null,
    }
  })
}

export type CcqDecidingState = Exclude<CcqCriterionState, typeof CcqCriterionStates.NOT_CHECKED>

// NOT IN RECORDS and MISSING DATA have no deciding criteria.
export const CCQ_DECIDING_STATE: Partial<Record<CcqCard, CcqDecidingState>> = {
  [CcqCards.MATCHED]: CcqCriterionStates.MET,
  [CcqCards.NOT_MATCHED]: CcqCriterionStates.NOT_MET,
  [CcqCards.CLOSER]: CcqCriterionStates.NOT_FOUND,
}

export const decidingCriteria = (criteria: CcqCriterionView[], card: CcqCard): CcqCriterionView[] => {
  const decidingState = CCQ_DECIDING_STATE[card]
  return decidingState === undefined ? [] : criteria.filter((criterion) => criterion.state === decidingState)
}

type CcqBucketKey = Pick<CrossClientQueryResultBucketCount, 'status' | 'match' | 'lastResolverStage'>

// A client the advisor moved, as the counts will have it once the move lands: one out of `from`, one into `to`. The bucket left
// is the one with their stage when there is one; a result read without its stage leaves any bucket of their status and match.
export const moveInBuckets = (buckets: CrossClientQueryResultBucketCount[], from: CcqBucketKey, to: CcqBucketKey): CrossClientQueryResultBucketCount[] => {
  const isOf = (key: CcqBucketKey) => (bucket: CrossClientQueryResultBucketCount) =>
    bucket.status === key.status && (bucket.match ?? null) === (key.match ?? null) && bucket.count > 0
  const ofStage = (key: CcqBucketKey) => (bucket: CrossClientQueryResultBucketCount) => (bucket.lastResolverStage ?? null) === (key.lastResolverStage ?? null)
  const exactIndex = buckets.findIndex((bucket) => isOf(from)(bucket) && ofStage(from)(bucket))
  const sourceIndex = exactIndex >= 0 ? exactIndex : buckets.findIndex(isOf(from))
  if (sourceIndex < 0) {
    return buckets
  }
  const stage = buckets[sourceIndex].lastResolverStage ?? null
  const target = { ...to, lastResolverStage: stage }
  const isTarget = (bucket: CrossClientQueryResultBucketCount) =>
    bucket.status === target.status && (bucket.match ?? null) === (target.match ?? null) && (bucket.lastResolverStage ?? null) === stage
  const shifted = buckets.map((bucket, index) => (index === sourceIndex ? { ...bucket, count: bucket.count - 1 } : bucket))
  return shifted.some(isTarget)
    ? shifted.map((bucket) => (isTarget(bucket) ? { ...bucket, count: bucket.count + 1 } : bucket))
    : [...shifted, { ...target, count: 1 }]
}

export const canChangeMatch = (result: Pick<CrossClientQueryResult, 'match'>): boolean => result.match !== null && result.match !== undefined

export interface CcqClientView {
  result: CrossClientQueryResult
  resultId: string
  clientId: string
  name: string
  firstName: string
  email: string
  owner: string
  card: CcqCard | null
  criteria: CcqCriterionView[]
  isAiChecking: boolean
}

export const toClientView = (result: CrossClientQueryResult, clauses: CcqCriterionClause[], client: User | undefined, owner: string): CcqClientView => {
  const card = cardForResult(result)
  const criteria = criterionStates(result, clauses)
  const name = getUserDisplayName(client)
  return {
    result,
    resultId: result.id,
    clientId: result.clientId,
    name,
    firstName: client?.firstName || name,
    email: client?.email ?? '',
    owner,
    card,
    criteria,
    isAiChecking: !!result.activeEvaluationId,
  }
}
